import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Undo2, Redo2, MoreHorizontal } from "lucide-react";

import { Separator } from "@/components/ui/separator";
import { Toggle } from "@/components/ui/toggle";
import { cn } from "@/lib/utils";
import { useEffect } from "react";
import icBold from "@/assets/icons/ic-bold.svg";
import icItalic from "@/assets/icons/ic-italic.svg";
import icUnderline from "@/assets/icons/ic-underline.svg";
import icX2 from "@/assets/icons/ic-x2.svg";
import icParagraph from "@/assets/icons/ic-paragraph.svg";
import icList from "@/assets/icons/ic-list.svg";
import icQuoto from "@/assets/icons/ic-quoto.svg";
export interface RichTextEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder = "내용 입력...",
  disabled = false,
  className,
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [StarterKit, Underline, Placeholder.configure({ placeholder })],
    content: value,
    editable: !disabled,
   onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    }
  });

  useEffect(() => {
  if (!editor) return;

  if (editor.getHTML() !== value) {
    editor.commands.setContent(value || "", {
      emitUpdate: false
    });
  }
}, [editor, value]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  if (!editor) return null;

  return (
    <div
      className={cn(
        "rounded border border-[##00000027]  bg-background ring-offset-background ",
        disabled && "cursor-not-allowed opacity-50",
        className,
      )}
    >
      <EditorToolbar editor={editor} disabled={disabled} />
      <EditorContent
        editor={editor}
        className="bg-white prose prose-sm max-w-none px-3 py-2 min-h-[150px] focus:outline-none [&_.tiptap]:outline-none [&_.tiptap_p.is-editor-empty:first-child::before]:text-muted-foreground [&_.tiptap_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.tiptap_p.is-editor-empty:first-child::before]:float-left [&_.tiptap_p.is-editor-empty:first-child::before]:h-0 [&_.tiptap_p.is-editor-empty:first-child::before]:pointer-events-none"
      />
    </div>
  );
}

function EditorToolbar({
  editor,
  disabled,
}: {
  editor: Editor;
  disabled: boolean;
}) {
  return (
    <div className="rounded-t flex items-center gap-[24px] px-4 py-2 bg-[#0000000b] ">
      {/* Text formatting: Bold, Italic, Underline, X2 */}
      <div className="flex items-center gap-0.5">
        <Toggle
          size="sm"
          pressed={editor.isActive("bold")}
          onPressedChange={() => editor.chain().focus().toggleBold().run()}
          disabled={disabled}
          aria-label="Bold"
        >
          <img src={icBold} alt="" className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive("italic")}
          onPressedChange={() => editor.chain().focus().toggleItalic().run()}
          disabled={disabled}
          aria-label="Italic"
        >
          <img src={icItalic} alt="" className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive("underline")}
          onPressedChange={() => editor.chain().focus().toggleUnderline().run()}
          disabled={disabled}
          aria-label="Underline"
        >
          <img src={icUnderline} alt="" className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive("heading", { level: 2 })}
          onPressedChange={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          disabled={disabled}
          aria-label="Heading"
        >
          <img src={icX2} alt="" className="h-4 w-4" />
        </Toggle>
      </div>

      {/* List: Paragraph, Bullet List, Ordered List, Quote */}
      <div className="flex items-center gap-0.5">
        <Toggle
          size="sm"
          pressed={editor.isActive("bulletList")}
          onPressedChange={() => editor.chain().focus().toggleBulletList().run()}
          disabled={disabled}
          aria-label="Paragraph"
        >
          <img src={icParagraph} alt="" className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive("orderedList")}
          onPressedChange={() => editor.chain().focus().toggleOrderedList().run()}
          disabled={disabled}
          aria-label="Ordered list"
        >
          <img src={icList} alt="" className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive("blockquote")}
          onPressedChange={() => editor.chain().focus().toggleBlockquote().run()}
          disabled={disabled}
          aria-label="Quote"
        >
          <img src={icQuoto} alt="" className="h-4 w-4" />
        </Toggle>
      </div>
    </div>
  );
}
